/**
 * What "finished" means on each kind of list, and where a finished line goes
 * — Doug, 18 Oct: "Groceries have the same problem; make sure that this
 * 'after checked state' applies to all of the various lists in a logical
 * way." The tasks plan (docs/tasks-plan, D1–D3) settled the shape for tasks:
 * the tick is written at once, the row lingers struck through, then leaves
 * the open list for a fold at the bottom. This file says the same for every
 * kind, so the phone's four list components and the desk's one draw from one
 * answer instead of five.
 *
 *   task        done_at     → "Done"            by day, newest first (taskRules)
 *   grocery     done_at     → "In the basket"   newest first; Clear basket deletes (L3: groceries recur)
 *   buy         bought      → "Bought"          by position (a status has no timestamp)
 *   collection  flagged_at  → the flag's word   newest first ("Seen · 3")
 *   template    —           never folds: a ticked step is progress on a run, not clutter
 *
 * Dependency-free (type-only import) for the root suite.
 */
import type { List, ListItem, ListKind } from './types'

type Line = Pick<ListItem, 'id' | 'done_at' | 'status' | 'flagged_at' | 'position'>

/** When the line was finished, as an ISO string for ordering — `''` for a
 *  finished purchase (no timestamp, so position decides), null when open. */
export function finishedAt(kind: ListKind, i: Line): string | null {
  switch (kind) {
    case 'task':
    case 'grocery': return i.done_at
    case 'buy': return i.status === 'bought' ? '' : null
    case 'collection': return i.flagged_at
    case 'template': return null
  }
}

export const isFinished = (kind: ListKind, i: Line): boolean => finishedAt(kind, i) !== null

/** The fold's head: "In the basket · 3", "Seen · 2". */
export function finishedHead(kind: ListKind, list: Pick<List, 'flag_label'>, n: number): string {
  const word = kind === 'task' ? 'Done'
    : kind === 'grocery' ? 'In the basket'
    : kind === 'buy' ? 'Bought'
    : kind === 'collection' ? capitalize(list.flag_label || 'flagged')
    : 'Done'
  return `${word} · ${n}`
}

export const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s)

/**
 * The split both shells draw: open lines (a lingering one still among them,
 * in its place — done in the data, not yet gone from the eye) and the fold,
 * newest finished first, position breaking ties. A template is never split.
 */
export function foldFinished<T extends Line>(kind: ListKind, items: readonly T[], lingering?: ReadonlySet<string>): { open: T[]; finished: T[] } {
  if (kind === 'template') return { open: [...items], finished: [] }
  const open: T[] = [], finished: T[] = []
  for (const i of items) (isFinished(kind, i) && !lingering?.has(i.id) ? finished : open).push(i)
  finished.sort((a, b) => {
    const x = finishedAt(kind, a) ?? '', y = finishedAt(kind, b) ?? ''
    return x < y ? 1 : x > y ? -1 : a.position - b.position
  })
  return { open, finished }
}
